/* eslint-disable react/no-children-prop */
import React from "react";
import { getRouteApi } from "@tanstack/react-router";
import { useTranslation, Trans } from "react-i18next";
// UI
import { Box, Image, Stack, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useAppForm } from "@components/form/app-form";
import { TwoColLayout } from "@components/two-col-layout";

import {
  SpringOnboardingText,
  SpringOnboardingTitle,
} from "@components/Typography";
import { useColors } from "@utils/useColors";
// data
import { useCheckSpringEligibilityMutation } from "src/backendApi/queries/spring-health-queries";

import ImageForThisPage from "src/assets/spring-kit-contents.png";
// util
import * as sentry from "@sentry/react";
import { z } from "zod";

const Route = getRouteApi("/spring-health/verify");

const EmailSchema = z.object({
  email: z.email(),
});

function PageTitle() {
  const themeColors = useColors();

  return (
    <SpringOnboardingTitle order={1} fw="800">
      <Trans
        i18nKey="springHealth.verify.pageTitle"
        components={{
          Mightier: (
            <Title
              ff="Rammetto One"
              fw="400"
              c={themeColors.tufts100}
              size={48}
            >
              MIGHTIER
            </Title>
          ),
        }}
      />
    </SpringOnboardingTitle>
  );
}

function CheckEligibilityForm() {
  const { t } = useTranslation();
  const navigate = Route.useNavigate();
  const searchParams = Route.useSearch();

  const { mutateAsync: checkEligibility } = useCheckSpringEligibilityMutation({
    onSuccess: (resp, vars) => {
      const { is_eligible: isEligible, does_account_exist: doesAccountExist } =
        resp;
      if (!isEligible) {
        notifications.show({
          title: t("springHealth.verify.submitNotifications.notEligibleTitle"),
          message: t("springHealth.verify.submitNotifications.notEligibleBody"),
          color: "yellow",
        });
      } else {
        navigate({
          from: "/spring-health/verify",
          to: doesAccountExist
            ? "/spring-health/register/log-in"
            : "/spring-health/register/sign-up",
          search: {
            email_address: vars.payload.email_address,
            spring_id: vars.payload.spring_id,
          },
        });
      }
    },
    onError: (err) => {
      sentry.captureException(err);
      notifications.show({
        title: t("springHealth.verify.submitNotifications.errorTitle"),
        message: t("springHealth.verify.submitNotifications.errorBody"),
        color: "red",
      });
    },
  });

  const form = useAppForm({
    defaultValues: {
      email: "",
    },
    validators: {
      onBlur: EmailSchema,
    },
    onSubmit: async ({ value }) => {
      await checkEligibility({
        payload: {
          email_address: value.email,
          spring_id: searchParams.spring_id,
        },
      });
    },
  });

  return (
    <Box w={{ xs: "100%", md: "50%" }}>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          await form.handleSubmit();
        }}
      >
        <form.AppField
          name="email"
          children={(field) => {
            return (
              <field.TextField
                errors={field.state.meta.errors}
                placeholder={t("loginPage.1_email")}
                type="email"
                data-testid="email"
              />
            );
          }}
        />

        <form.Subscribe
          selector={(state) => [
            state.canSubmit,
            state.isSubmitting,
            state.isDefaultValue,
          ]}
          children={([canSubmit, isSubmitting, isDefaultValue]) => (
            <form.SubmitButton
              loading={isSubmitting}
              disabled={!canSubmit || isDefaultValue}
              mt="md"
              data-testid="check-eligible-btn"
            >
              {t("springHealth.verify.button")}
            </form.SubmitButton>
          )}
        />
      </form>
    </Box>
  );
}

export function SpringHealthEligibilityPage() {
  const { t } = useTranslation();

  return (
    <Box mt="15%" data-testid="hello">
      <TwoColLayout
        leftChildren={
          <Stack px="sm" mt={{ xs: 0, md: "xl" }}>
            <PageTitle />
            <SpringOnboardingText size="lg">
              {t("springHealth.verify.pageDescription")}
            </SpringOnboardingText>

            <SpringOnboardingText>
              <Trans
                i18nKey="springHealth.verify.enterEmailLabel"
                components={{ b: <b /> }}
              />
            </SpringOnboardingText>

            <CheckEligibilityForm />
          </Stack>
        }
        rightChildren={<Image src={ImageForThisPage} />}
      />
    </Box>
  );
}
